<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">Who Maintains Spin the Names?</h2>

    <div class="mb-6">
      <p>Spin the Names is built and maintained by Ash, an independent software engineer based in Austin, TX. It's not
        a corporate product — there's no team of product managers, no quarterly roadmap meetings, no growth hackers
        trying to maximize engagement at the expense of the experience. Just one developer who wants to build something
        useful and keep it working well.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Why independent?</h3>
      <p>Most name picker tools on the internet are owned by large companies with advertising businesses, data
        collection incentives, or both. Independent software doesn't have those constraints. The goal here is a tool
        that works well, respects users, and stays honest about what it is and isn't.</p>
      <p class="mt-2">That means no dark patterns, no manipulative design, no features engineered to maximize
        time-on-site. If the wheel does its job in 10 seconds and you close the tab, that's a success.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Development philosophy</h3>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>Stability first.</strong> The core experience should work reliably every time. New features
          don't ship until the foundation is solid.</li>
        <li><strong>Performance matters.</strong> The wheel should load fast, spin smoothly, and not bloat over
          time. Every dependency is a liability.</li>
        <li><strong>Accessibility by default.</strong> The app should work across devices, browsers, and
          network conditions. Not just for users on fast Wi-Fi and new hardware.</li>
        <li><strong>Honest about limitations.</strong> Small teams can't do everything. The roadmap is
          shaped by what genuinely improves the tool, not what makes for a good press release.</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">What drives the roadmap?</h3>
      <p>Feature decisions are driven by user feedback, real usage patterns, and the developer's own judgment
        about what makes the tool better. There's no investor pressure to add features for growth metrics.
        If something makes the core experience more reliable, more useful, or more fair — it gets prioritized.
        If it adds complexity without clear benefit, it doesn't.</p>
      <p class="mt-2">The most impactful features have consistently come from teachers, event organizers, and
        community members who reached out with specific, grounded requests. That feedback loop is something
        a small independent project does better than a large company.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Long-term outlook</h3>
      <p>The plan is for Spin the Names to keep improving and stay free for its core use cases. The app is
        supported by ads and will eventually offer optional premium features for users who want more. But the
        fundamental tool — spin a wheel, get a fair result — will always be free and always be the priority.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Contact</h3>
      <p>Questions, feedback, or just want to say the app helped your classroom? Reach out at
        <a href="mailto:ash@spinthenames.com" class="text-blue-500 hover:underline">ash@spinthenames.com</a>.
        Everything gets read, though response times vary depending on workload.</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
